import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { Button } from '@unified-checkout/design-system';
import {
  ArrowDown,
  ArrowRightArrowLeft,
  ArrowsRepeat,
  ArrowUpRight,
  ArrowUpRightFromSquare,
  Calendar,
  CircleMinus,
  MoneyBill,
  MoneyBillsSimple,
  MoneySimpleFromBracket,
  Receipt,
  TruckMoving,
  Wallet,
} from '@unified-checkout/design-system/icons/index';

import type { ISummaryCard } from '@app/dashboard/types/innerLayout/balance_and_transfers/transfers/summary';
import type { IBreakdownProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer/breakdown';
import { useLangStore } from '@app/dashboard/store/lang';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';

import {
  getSharedEventProperties,
  handleStoreLastAction,
  viewRelatedTransactionsEventTracking,
} from '../../../../../utils/events-tracking';
import {
  formatAmountWithCurrency,
  viewRelatedTransactions,
} from '../../../../../utils';
import SummaryCard from '../../../../../components/SummaryCard';

import styles from './detailed-view.module.scss';

const DetailedView = ({
  breakdown,
  isLoading,
  transferId,
  header,
}: IBreakdownProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const {
    currency = '',
    amount_deposit = 0,
    balance_transfer = 0,
    chargeback_amount = 0,
    delivery_fee = 0,
    gross_amount = 0,
    net_amount = 0,
    processing_fee = 0,
    refund_amount = 0,
    subscription_amount = 0,
    total_deductions = 0,
    total_fees = 0,
    transaction_count,
    transfer_fee = 0,
    vat = 0,
  } = breakdown || {};

  const formattedAmount = (amount: number) => {
    return formatAmountWithCurrency({
      amount,
      currency,
      lang,
    });
  };

  const getRelatedTransactions = () => {
    viewRelatedTransactionsEventTracking({
      currency,
      status: header?.status?.key || '',
      transactionCount: transaction_count || 0,
      transferId: transferId || '',
    });
    viewRelatedTransactions(transferId || '');
  };

  const SUMMARY_CARD_ITEMS: ISummaryCard['itemsList'] = [
    {
      label: {
        renderAs: (
          <div className={styles['detailed-view__gross-amount']}>
            <span className={styles['detailed-view__gross-amount__label']}>
              {t(
                'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.grossAmountOnly'
              )}
            </span>
            (
            <Button
              trailingIcon={ArrowUpRightFromSquare}
              variant="link"
              color="primary"
              size="md"
              text={t(
                'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.viewTransactions',
                {
                  count: transaction_count,
                }
              )}
              onClickHandler={() => getRelatedTransactions()}
            />
            )
          </div>
        ),
      },
      labelLeadingIcon: Wallet,
      value: {
        text: formattedAmount(gross_amount),
        isBold: true,
      },
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.netAmount'
      ),
      labelLeadingIcon: MoneyBill,
      value: {
        text: formattedAmount(net_amount),
        isBold: true,
        status: 'success',
      },
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.totalFees'
      ),
      labelLeadingIcon: MoneySimpleFromBracket,
      value: `- ${formattedAmount(total_fees)}`,
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.processingFees'
      ),
      labelLeadingIcon: Receipt,
      value: `- ${formattedAmount(processing_fee)}`,
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.vat'
      ),
      labelLeadingIcon: MoneyBillsSimple,
      value: `- ${formattedAmount(vat)}`,
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.transferFeeAmount'
      ),
      labelLeadingIcon: ArrowUpRight,
      value: `- ${formattedAmount(transfer_fee)}`,
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.deliveryFeeAmount'
      ),
      labelLeadingIcon: TruckMoving,
      value: `- ${formattedAmount(delivery_fee)}`,
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.subscriptionAmount'
      ),
      labelLeadingIcon: Calendar,
      value: `- ${formattedAmount(subscription_amount)}`,
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.amountDeposit'
      ),
      labelLeadingIcon: ArrowDown,
      value: {
        text: `± ${formattedAmount(amount_deposit)}`,
        isBold: true,
        status: 'success',
      },
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.amountBalanceTransfer'
      ),
      labelLeadingIcon: ArrowsRepeat,
      value: `± ${formattedAmount(balance_transfer)}`,
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.refundAmount'
      ),
      labelLeadingIcon: ArrowRightArrowLeft,
      value: `- ${formattedAmount(refund_amount)}`,
    },
    {
      label: t(
        'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.chargebackAmount'
      ),
      labelLeadingIcon: CircleMinus,
      value: `- ${formattedAmount(chargeback_amount)}`,
    },
    {
      label: {
        text: t(
          'innerLayout.balanceTransfers.singleTransfer.breakdown.fields.totalDeduction'
        ),
        isBold: true,
      },
      value: `- ${formattedAmount(total_deductions)}`,
    },
  ];

  const handleExpandClickTracker = (expanded: boolean) => {
    const eventName =
      BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.BREAKDOWN_EXPANDED;

    amplitude.track(eventName, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE.IS_EXPANDED]:
        expanded,
      ...getSharedEventProperties({
        transferId: transferId ?? '',
        status: header?.status?.key ?? '',
        currency: header?.currency ?? '',
      }),
    });

    handleStoreLastAction(eventName);
  };

  return (
    <SummaryCard
      itemsList={SUMMARY_CARD_ITEMS}
      collapsible={{
        isExpandableCentred: true,
        showCollapseButton: true,
        visibleCount: 5,
        renderAsRow: true,
        expandableButton: {
          onClickHandler: handleExpandClickTracker,
        },
      }}
      isLoading={isLoading}
    />
  );
};

export default DetailedView;
